import type {
  AxisLayoutOptions,
  CrosshairStyle,
  ReferenceLine,
  SeriesToggleStyle,
} from '@morfi/chart';

export const liveChartAxisOptions: AxisLayoutOptions = {
  y: { labelRightMargin: 4, gridEndGap: 24 },
};

/** Main-branch market legend styling: compact content without filled chips. */
export const liveChartLegendStyle: SeriesToggleStyle = {
  fontSize: 12,
  borderRadius: 8,
  dotSize: 8,
  containerBackground: 'transparent',
  activeBackground: 'transparent',
  hiddenBackground: 'transparent',
  activeColor: 'var(--foreground-secondary)',
  hiddenColor: 'var(--foreground-tertiary)',
};

export function buildPerSeriesTooltip({
  formatSeriesValue,
  formatTimeRange,
  alwaysShow = false,
}: {
  formatSeriesValue: (value: number) => string;
  formatTimeRange: (from: number, to: number) => string;
  alwaysShow?: boolean;
}): CrosshairStyle {
  return {
    layout: 'per-series-pill',
    alwaysShow,
    guideColor: resolveCssVar('var(--border-primary)', 'rgba(255,255,255,0.35)'),
    guideWidth: 1,
    guideDashPattern: [3, 3],
    timePillBackground: resolveCssVar('var(--background-tertiary)', '#1f1f23'),
    timePillColor: resolveCssVar('var(--foreground-primary)', '#fff'),
    timePillRadius: 6,
    timePillPaddingX: 8,
    timePillPaddingY: 4,
    timePillFontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    timePillFontSize: 11,
    timePillFontWeight: 500,
    formatTimeRange,
    seriesPillBackground: resolveCssVar('var(--background-tertiary)', '#1f1f23'),
    seriesPillColor: resolveCssVar('var(--foreground-secondary)', '#a1a1aa'),
    seriesPillRadius: 6,
    seriesPillPaddingX: 8,
    seriesPillPaddingY: 4,
    seriesPillDotSize: 8,
    seriesPillDotGap: 6,
    seriesPillLabelValueGap: 6,
    seriesPillFontFamily: 'system-ui, -apple-system, sans-serif',
    seriesPillFontSize: 12,
    seriesPillFontWeight: 400,
    seriesPillValueFontWeight: 500,
    seriesPillValueColor: resolveCssVar('var(--foreground-primary)', '#fff'),
    formatSeriesValue,
    maxLabelChars: 14,
  };
}

/** Flatten a single CSS variable into a canvas-paintable color. */
export function resolveCssVar(input: string, fallback: string = input): string {
  if (typeof document === 'undefined') return fallback;
  const match = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(input);
  if (match === null) return input;
  const variableName = match[1];
  if (variableName === undefined) return fallback;
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  if (resolved.length > 0) return resolved;
  return match[2]?.trim() ?? fallback;
}

/** Upstream Liveline supports one horizontal reference line. */
export function zeroBaselineReferenceLine(): ReferenceLine {
  return { value: 0 };
}
